import { css } from '@emotion/react';
import {
  Box,
  Button,
  COLOR_PALETTE,
  EmptyStates,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react-next';
import _ from 'lodash-es';
import { useTranslation } from 'next-i18next';
import useNextUrlState from '../../../../hooks/useNextUrlState';
import useNoteMeQuery from '../../../../queries/useNoteMeQuery';
import { NoteItem } from './NoteItem';
import type { NoteSearchType, NoteSort } from './types';

export const NoteList = () => {
  const { t } = useTranslation();

  const { value: query, fetchQuery } = useNextUrlState<{
    sort?: NoteSort;
    searchKeyword?: string;
    searchType?: NoteSearchType;
  }>();
  const { data: notes } = useNoteMeQuery({ ...query }, { suspense: true, useErrorBoundary: true });

  if (_.isNil(notes)) {
    return null;
  }

  if (!_.isEmpty(query?.searchKeyword) && _.isEmpty(notes)) {
    return (
      <Box my={90}>
        <EmptyStates
          image="NO_RESULT"
          title={t('검색 결과가 없어요')}
          body={t('검색 기준을 변경하거나 검색어를 확인해 주세요.')}
          cta={
            <Button variant="default" size="xs" onClick={() => fetchQuery({ searchKeyword: '' })}>
              {t('검색 초기화')}
            </Button>
          }
        />
      </Box>
    );
  }

  if (_.isEmpty(notes)) {
    return (
      <Box my={90}>
        <EmptyStates
          image="NO_DATA"
          title={t('작성한 노트가 없어요')}
          body={t('강의를 수강하고 나만의 노트를 작성해 보세요!')}
        />
      </Box>
    );
  }

  return (
    <Box component="ul" css={styleNoteList}>
      {notes.map((note, index) => (
        <li
          key={note.id}
          css={{
            borderTop: index === 0 ? `1px solid ${COLOR_PALETTE.gray[2]}` : 'unset',
            borderBottom: `1px solid ${COLOR_PALETTE.gray[2]}`,
          }}
        >
          <NoteItem
            id={note.id}
            title={note.course.title}
            thumbnailSrc={note.course.coverFile}
            accessedAt={note.course.lastStudiedAtUTC}
            isPublic={note.isPublic}
            noteCount={note.count}
          />
        </li>
      ))}
    </Box>
  );
};

const styleNoteList = css({
  marginBottom: 80,

  [desktopFirstMediaQuery.tablet]: {
    marginBottom: 40,
  },
});
